import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  fetchSystem,
} from '../../../states/containers/setting/systemSlice'

import {
  createGoogleSync,
  deleteGoogleSync,
  GoogleSyncState,
} from '../../../states/containers/setting/googleSyncSlice'
import {
  createMicrosoftSync,
  deleteMicrosoftSync,
  MicrosoftSyncState,
} from '../../../states/containers/setting/microsoftSyncSlice'
import {
  createCybozuSync,
  deleteCybozuSync,
  CybozuSyncState,
} from '../../../states/containers/setting/cybozuSyncSlice'
import { useTranslation } from 'react-i18next'
import { useAlert } from 'react-alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format } from '../../../utils/date'
import { getGetPrams } from '../../../utils/url'
import Loading from '../../components/common/Loading'
import { RootState } from '../../../states/containers/store'

type Servicename = 'google' | 'microsoft' | 'cybozu'

interface GetParams {
  account_setting_error: Servicename
}

const OauthInfo: FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const alert = useAlert()

  const {
    googleSync,
    microsoftSync,
    cybozuSync,
    system,
  } = useSelector((state: RootState) => (state))

  const [accountSettingError, setAccountSettingError] = useState<Servicename | undefined>(undefined)

  const handleGoogleSync = () => {
    dispatch(
      createGoogleSync((google: GoogleSyncState) => {
        location.href = google.login_url
      }),
    )
  }

  const handleGoogleDisassociation = () => {
    dispatch(
      deleteGoogleSync(() => {
        dispatch(
          fetchSystem(() => {
            alert.success(t('setting.personal.account.message.delete_sync'))
          }),
        )
      }),
    )
  }

  const handleMicrosoftSync = () => {
    dispatch(
      createMicrosoftSync((microsoft: MicrosoftSyncState) => {
        location.href = microsoft.login_url
      }),
    )
  }

  const handleMicrosoftDisassociation = () => {
    dispatch(
      deleteMicrosoftSync(() => {
        dispatch(
          fetchSystem(() => {
            alert.success(t('setting.personal.account.message.delete_sync'))
          }),
        )
      }),
    )
  }

  const handleCybozuSync = () => {
    dispatch(
      createCybozuSync((cybozu: CybozuSyncState) => {
        location.href = cybozu.login_url
      }),
    )
  }

  const handleCybozuDisassociation = () => {
    dispatch(
      deleteCybozuSync(() => {
        dispatch(
          fetchSystem(() => {
            alert.success(t('setting.personal.account.message.delete_sync'))
          }),
        )
      }),
    )
  }

  const displaySyncedButton = () => {
    let servicename
    let handleEvent
    let detail
    if (system.google_account) {
      servicename = 'google'
      handleEvent = handleGoogleDisassociation
      detail = system.google_account
    } else if (system.microsoft_account) {
      servicename = 'microsoft'
      handleEvent = handleMicrosoftDisassociation
      detail = system.microsoft_account
    } else {
      servicename = 'cybozu'
      handleEvent = handleCybozuDisassociation
      detail = system.cybozu_account
    }
    return (
      <div className="box-body calendar-setting">
        <label htmlFor={`sign_out_${servicename}`}>
          {t(`setting.personal.account.${servicename}_account`)}
        </label>
        <div className="linked_detail">
          {detail?.email}
          <span>
            {format(detail?.link_time, 'YYYY/MM/DD HH:mm')}
            {t(`setting.personal.account.linked`)}
          </span>
        </div>
        <button
          type="button"
          id={`sign_out_${servicename}`}
          className={`btn sso-${servicename}`}
          onClick={handleEvent}
        >
          <img src={`/static/images/logo_${servicename}.svg`} alt={servicename} />
          {t(`setting.personal.account.${servicename}_sign_out`)}
        </button>
      </div>
    )
  }

  const displaySyncError = (servicename: Servicename) => (
    <div className="account_setting_error">
      <p className="error_title">
        <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
        <span>{t(`setting.personal.account.error.${servicename}.title`)}</span>
      </p>
      {/* eslint-disable-next-line react/no-danger */}
      <p dangerouslySetInnerHTML={{ __html: t(`setting.personal.account.error.${servicename}.description`) }} />
    </div>
  )

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.account_setting_error
      && (
        getParams.account_setting_error.includes('google')
        || getParams.account_setting_error.includes('microsoft')
        || getParams.account_setting_error.includes('cybozu')
      )
    ) {
      setAccountSettingError(getParams.account_setting_error)
    }
  }, [])

  const renderSyncAccount = () => (
    !system.google_account && !system.microsoft_account && !system.cybozu_account ? (
      <div className="sso-settings">
        <div className="box-body calendar-setting">
          <label htmlFor="sign_in_google">
            {t('setting.personal.account.google_account')}
          </label>
          <button
            type="button"
            id="sign_in_google"
            className="btn sso-google"
            onClick={handleGoogleSync}
          >
            <img src="/static/images/logo_google.svg" alt="Google" />
            {t('setting.personal.account.google_sign_in')}
          </button>
        </div>
        <div className="joint">
          or
        </div>
        <div className="box-body calendar-setting">
          <label htmlFor="sign_in_microsoft">
            {t('setting.personal.account.microsoft_account')}
          </label>
          <button
            type="button"
            id="sign_in_microsoft"
            className="btn sso-microsoft"
            onClick={handleMicrosoftSync}
          >
            <img src="/static/images/logo_microsoft.svg" alt="Microsoft" />
            {t('setting.personal.account.microsoft_sign_in')}
          </button>
        </div>
        {system.is_available_cybozu ? (
          <>
            <div className="joint">
              or
            </div>
            <div className="box-body calendar-setting">
              <label htmlFor="sign_in_cybozu">
                {t('setting.personal.account.cybozu_account')}
              </label>
              <button
                type="button"
                id="sign_in_cybozu"
                className="btn sso-cybozu"
                onClick={handleCybozuSync}
              >
                <img src="/static/images/logo_cybozu.svg" alt="Cybozu" />
                {t('setting.personal.account.cybozu_sign_in')}
              </button>
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
    ) : (
      displaySyncedButton()
    )
  )

  return (
    <div className="sso-area">
      {
        accountSettingError && (
          displaySyncError(accountSettingError)
        )
      }
      {
        googleSync.isSucceeded === null && microsoftSync.isSucceeded === null && cybozuSync.isSucceeded === null ? (
          <Loading />
        ) : (
          renderSyncAccount()
        )
      }
      <div className="box-body calendar-setting">
        <p>{t('setting.personal.account.message.calendar_sync')}</p>
      </div>
    </div>
  )
}

export default OauthInfo
